// Full-res "Take a better picture" recapture. Opens the live camera, captures a
// JPEG from a canvas, lets you CROP it to just the artwork, then re-runs
// enrichment IN PLACE on the same artifact: POST /artifacts/{id}/reupload -> PUT
// the better (cropped) photo over <id>/original.jpg -> POST .../enrich. Same
// ULID, same record, so manual edits are preserved (fieldSources=manual;
// CONTRACTS §4). Cropping tightens the frame for better online matches and a
// cleaner fallback image (DESIGN §7). CONTRACTS §7/§10, DESIGN §9.

import { useCallback, useRef, useState } from "react";
import { captureJpegFromVideo, useCamera } from "../camera";
import { recaptureAndEnrich, recaptureOriginalOnly } from "../api";
import { CropModal } from "./CropModal";

export interface RecaptureModalProps {
  /** The existing (placeholder) artifact being re-captured in place. */
  artifactId: string;
  onClose: () => void;
  /**
   * Called after the better photo was uploaded over the SAME artifact's
   * original.jpg (and, in re-enrich mode, re-enrichment was triggered). The parent
   * should re-fetch (the record keeps its id; in re-enrich mode it re-enters the
   * needs_review queue).
   */
  onRecaptured: (id: string) => void;
  /**
   * When false, only the original photo BYTES are restored (recaptureOriginalOnly)
   * — enrichment is NOT re-run, so existing metadata/canonical is preserved. Used
   * to repair an artifact whose original.jpg 404s (continuation 2026-06-07 P1b).
   * Defaults to true (the "take a better picture" re-enrich path).
   */
  enrichAfter?: boolean;
}

export function RecaptureModal({
  artifactId,
  onClose,
  onRecaptured,
  enrichAfter = true,
}: RecaptureModalProps) {
  const camera = useCamera(true);
  const [captured, setCaptured] = useState<Blob | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const captureLock = useRef(false);

  const capture = useCallback(async () => {
    if (captureLock.current) return;
    const video = camera.videoRef.current;
    if (!video) return;
    captureLock.current = true;
    setError(null);
    try {
      const jpeg = await captureJpegFromVideo(video);
      if (!jpeg) {
        setError("Could not capture a frame — try again.");
        return;
      }
      setCaptured(jpeg); // -> show the cropper (camera stays live behind it)
    } finally {
      captureLock.current = false;
    }
  }, [camera]);

  const submit = useCallback(
    async (blob: Blob) => {
      setBusy(true);
      setError(null);
      try {
        // In-place: overwrite this artifact's original.jpg. In re-enrich mode also
        // re-run enrichment; in original-only mode (repair a broken original) leave
        // the existing metadata/canonical untouched.
        if (enrichAfter) {
          await recaptureAndEnrich(artifactId, blob);
        } else {
          await recaptureOriginalOnly(artifactId, blob);
        }
        camera.stop();
        onRecaptured(artifactId);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Recapture failed.");
        setBusy(false); // stay on the cropper so they can retry / cancel
      }
    },
    [artifactId, camera, onRecaptured, enrichAfter],
  );

  const close = () => {
    camera.stop();
    onClose();
  };

  // Mode-specific copy: re-enrich ("take a better picture") vs. original-only
  // (repair a broken original without disturbing the existing match/metadata).
  const heading = enrichAfter ? "Take a better picture" : "Re-attach original photo";
  const cropConfirmLabel = enrichAfter ? "Use & re-enrich" : "Use photo — keep enrichment";
  const note = enrichAfter
    ? "This captures a fresh full-res photo, lets you crop to just the artwork, replaces this item's original image, and re-runs enrichment on it. Your manual edits are kept; it returns to the Review queue."
    : "This item's original photo is missing. Capture a fresh photo to re-attach it. The existing match and all metadata are kept — enrichment is NOT re-run.";

  // Step 2 — crop the captured frame, then submit on confirm.
  if (captured) {
    return (
      <CropModal
        source={captured}
        title="Crop the new photo"
        confirmLabel={cropConfirmLabel}
        busy={busy}
        error={error}
        onCancel={() => {
          if (busy) return;
          setCaptured(null); // back to the live camera
          setError(null);
        }}
        onConfirm={(blob) => void submit(blob)}
      />
    );
  }

  // Step 1 — live camera.
  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label={heading}>
      <div className="modal">
        <header className="modal__header">
          <h2>{heading}</h2>
          <button type="button" className="btn btn--ghost" onClick={close} disabled={busy}>
            Cancel
          </button>
        </header>

        <p className="modal__note">{note}</p>

        <div className="camera-stage camera-stage--modal">
          <video ref={camera.videoRef} className="camera-video" playsInline muted />
          {camera.status !== "ready" && (
            <div className="camera-overlay">
              {camera.status === "error"
                ? camera.error ?? "Camera unavailable"
                : "Starting camera…"}
            </div>
          )}
        </div>

        {error && <p className="form-error">{error}</p>}

        <footer className="modal__footer">
          <button
            type="button"
            className="btn btn--primary btn--large"
            onClick={capture}
            disabled={busy || camera.status !== "ready"}
          >
            Capture
          </button>
        </footer>
      </div>
    </div>
  );
}
